import { Fragment } from "react/jsx-runtime";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import type { IconName } from "../Icon";
import { Text } from "../Text";
import { userDisplayName } from "../../lib/userDisplayName";
import { ActivityLine } from "./ActivityLine";
import { Message } from "./transcriptStyles";
import { getActionDisplay } from "./UserActionGroup";

const userMessageActionDisplay = {
  readme: { icon: "pencil", message: "asked 3B to add a readme" },
  error: { icon: "traffic-cone", message: "sent an error message to 3B" },
  connector: {
    icon: "api-key-custom",
    message: "asked 3B to set up this workflow as a connector",
  },
  investigate_step_test: {
    icon: "radar",
    message: "asked 3B to fix step test failures",
  },
  investigate_step_status: {
    icon: "traffic-cone",
    message: "asked 3B to investigate failing steps",
  },
  step_test_fixture_setup: {
    icon: "microscope",
    message: "asked 3B to add a step test",
  },
  add_tests: { icon: "microscope", message: "asked 3B to add tests" },
} satisfies Record<string, { icon: IconName; message: string }>;

export function resolveActionLine(entry: ChatEntryWithSubTables): {
  icon: IconName;
  iconColor?: string;
  content: React.ReactNode;
} | null {
  if (entry.entryType === "UserAction" && entry.userAction) {
    const action = entry.userAction;
    if (action.actionType === "pushDraftToLive") {
      const user = action.user;
      return {
        icon: "rocket",
        content: (
          <>
            <Text variant="text-small-md" color="var(--text-primary)">
              {user ? userDisplayName(user) : "Someone"}
            </Text>{" "}
            pushed{" "}
            <Text variant="text-small-md" color="var(--text-primary)">
              {action.metadata.draftName}
            </Text>{" "}
            live
          </>
        ),
      };
    }
    const { icon, iconColor, text } = getActionDisplay(action);
    return { icon, iconColor, content: text };
  }
  const actionKind = entry.userMessage?.actionKind;
  if (
    entry.entryType === "UserMessage" &&
    actionKind &&
    actionKind in userMessageActionDisplay
  ) {
    const user = entry.userMessage?.user;
    const display =
      userMessageActionDisplay[
        actionKind as keyof typeof userMessageActionDisplay
      ];
    return {
      icon: display.icon,
      content: (
        <>
          <Text variant="text-small-md" color="var(--text-primary)">
            {user ? userDisplayName(user) : "Someone"}
          </Text>{" "}
          {display.message}
        </>
      ),
    };
  }
  return null;
}

type Props = {
  actionLine: NonNullable<ReturnType<typeof resolveActionLine>>;
  dateMarker: React.ReactNode;
  hasPrev: boolean;
  hasNext: boolean;
};

export function ActivityLineRow({
  actionLine,
  dateMarker,
  hasPrev,
  hasNext,
}: Props) {
  return (
    <Fragment>
      {dateMarker}
      <Message
        style={{
          marginTop: hasPrev ? 16 : 0,
          marginBottom: hasNext ? 16 : 0,
        }}
      >
        <ActivityLine icon={actionLine.icon} iconColor={actionLine.iconColor}>
          {actionLine.content}
        </ActivityLine>
      </Message>
    </Fragment>
  );
}
